import { EmployeeRecord, ForgotPasswordRequest, LoginRequest, LoginResponse, RegisterRequest, RegisterResponse, ResetPasswordRequest, UpdateUserRequest } from '../types/auth';
import { Material, CreateMaterialRequest } from '../types/material';
import { Order, OrderCreateRequest, OrderStats } from '../types/order';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const parseErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const text = await response.text();

  if (!text) {
    if (response.status === 401) return 'Sessao expirada. Faca login novamente.';
    if (response.status === 403) return 'Sem permissao para executar esta acao.';
    return fallback;
  }

  try {
    const parsed = JSON.parse(text) as { error?: string; message?: string };
    return parsed.error || parsed.message || fallback;
  } catch {
    return text;
  }
};

/**
 * Tenta renovar o access token via refresh token (cookie httpOnly).
 * Usa um padrao de lock para evitar multiplas requisicoes simultaneas.
 */
const tryRefreshToken = async (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Wrapper que adiciona refresh automatico em caso de 401.
 * Se a requisicao original retornar 401, tenta renovar o token
 * e, se bem-sucedido, refaz a requisicao original.
 */
const fetchWithRefresh = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const response = await fetch(url, { ...options, credentials: 'include' });

  if (response.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      // Refaz a requisicao original com o novo cookie
      return fetch(url, { ...options, credentials: 'include' });
    }
  }

  return response;
};

export const authService = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha no login'));
    }

    return response.json();
  },

  refresh: async (): Promise<boolean> => {
    return tryRefreshToken();
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<{ message: string }> => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao enviar e-mail de recuperacao'));
    }

    return response.json();
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<{ message: string }> => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao redefinir senha'));
    }

    return response.json();
  },

  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao cadastrar usuário'));
    }

    return response.json();
  },

  listUsers: async (): Promise<EmployeeRecord[]> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/auth/users`);

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao buscar usuários'));
    }

    const json = await response.json();
    // O backend retorna { data: [...], total, page, perPage, totalPages }
    return json.data ?? json;
  },

  updateUser: async (id: number, data: UpdateUserRequest): Promise<{ message: string }> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/auth/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao atualizar usuário'));
    }

    return response.json();
  },

  toggleUserStatus: async (id: number, ativo: boolean): Promise<{ message: string }> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/auth/users/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ativo }),
    });

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao atualizar status do usuário'));
    }

    return response.json();
  },

  me: async (): Promise<LoginResponse | null> => {
    try {
      const response = await fetchWithRefresh(`${API_BASE_URL}/auth/me`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  listActivityLogs: async (page: number = 1): Promise<{ data: any[]; total: number; page: number; perPage: number; totalPages: number }> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/auth/activity-logs?page=${page}&perPage=50`);
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao carregar logs'));
    }
    return response.json();
  },

  getHealth: async (): Promise<{ status: string; service: string; version: string }> => {
    const response = await fetch(`${API_BASE_URL}/health`);
    if (!response.ok) {
      return { status: 'error', service: 'backend-go', version: '0.0.0' };
    }
    return response.json();
  },

  logout: async (): Promise<void> => {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  },
};

export const orderService = {
  getAll: async (params?: Record<string, string>): Promise<Order[]> => {
    let url = `${API_BASE_URL}/api/orders`;
    if (params) {
      const qs = new URLSearchParams(params).toString();
      if (qs) url += `?${qs}`;
    }
    const response = await fetchWithRefresh(url);
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar ordens'));
    const json = await response.json();
    return json.data ?? json;
  },

  getOpen: async (params?: Record<string, string>): Promise<Order[]> => {
    let url = `${API_BASE_URL}/api/orders/open`;
    if (params) {
      const qs = new URLSearchParams(params).toString();
      if (qs) url += `?${qs}`;
    }
    const response = await fetchWithRefresh(url);
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar ordens abertas'));
    const json = await response.json();
    return json.data ?? json;
  },

  getAllPages: async (params?: Record<string, string>): Promise<Order[]> => {
    const all: Order[] = [];
    let page = 1;
    const perPage = 300;
    while (true) {
      const merged = { perPage: String(perPage), page: String(page), ...params };
      let url = `${API_BASE_URL}/api/orders`;
      const qs = new URLSearchParams(merged).toString();
      if (qs) url += `?${qs}`;
      const response = await fetchWithRefresh(url);
      if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar ordens'));
      const json = await response.json();
      const data = (json.data ?? json) as Order[];
      all.push(...data);
      const total = json.total as number | undefined;
      if (data.length === 0) break;
      if (total != null && all.length >= total) break;
      page += 1;
    }
    return all;
  },

  getOpenPages: async (params?: Record<string, string>): Promise<Order[]> => {
    const all: Order[] = [];
    let page = 1;
    const perPage = 300;
    while (true) {
      const merged = { perPage: String(perPage), page: String(page), ...params };
      let url = `${API_BASE_URL}/api/orders/open`;
      const qs = new URLSearchParams(merged).toString();
      if (qs) url += `?${qs}`;
      const response = await fetchWithRefresh(url);
      if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar ordens abertas'));
      const json = await response.json();
      const data = (json.data ?? json) as Order[];
      all.push(...data);
      const total = json.total as number | undefined;
      if (data.length === 0) break;
      if (total != null && all.length >= total) break;
      page += 1;
    }
    return all;
  },

  getStats: async (start?: string, end?: string): Promise<OrderStats> => {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const qs = params.toString();
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/stats${qs ? `?${qs}` : ''}`);
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar estatísticas'));
    return response.json();
  },

  create: async (data: OrderCreateRequest): Promise<Order> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      if (response.status === 409) {
        const body = await response.json();
        const err = new Error(body.error || 'Já existe uma ordem de serviço não concluída com este barramento') as any;
        err.protocolNumber = body.protocolNumber;
        err.existingOrderId = body.existingOrderId;
        err.statusCode = 409;
        throw err;
      }
      throw new Error(await parseErrorMessage(response, 'Falha ao criar ordem'));
    }
    return response.json();
  },

  update: async (id: number, data: Partial<OrderCreateRequest>): Promise<Order> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao atualizar ordem'));
    }
    return response.json();
  },

  markReceiptPrinted: async (id: number): Promise<Order> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/${id}/receipt/printed`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response, 'Falha ao registrar impressão do comprovante'));
    }
    return response.json();
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao excluir ordem'));
  },

  getAuditLogs: async (orderId: number): Promise<{ id: number; action: string; message: string; performedBy: string; performedAt: string }[]> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/${orderId}/audit-logs`);
    if (!response.ok) throw new Error('Falha ao buscar histórico da ordem');
    return response.json();
  },

  searchByCPF: async (cpf: string, birthDate?: string, page = 1, perPage = 10): Promise<{ data: Order[]; total: number; page: number; perPage: number; totalPages: number }> => {
    const params = new URLSearchParams({ cpf: cpf.replace(/\D/g, ''), page: String(page), perPage: String(perPage) });
    if (birthDate) params.set('birthDate', birthDate);
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/public?${params.toString()}`);
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar OS públicas'));
    return response.json();
  },

  getPoleBusbarLocation: async (poleBusbar: string): Promise<{ latitude: number; longitude: number; address: string; neighborhood?: string } | null> => {
    try {
      const normalizedPoleBusbar = poleBusbar.trim().toUpperCase();
      const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/pole-busbar/${encodeURIComponent(normalizedPoleBusbar)}`);
      if (!response.ok) return null;
      const data = await response.json() as { found: boolean; latitude: number; longitude: number; address: string; neighborhood?: string };
      return data.found ? { latitude: data.latitude, longitude: data.longitude, address: data.address, neighborhood: data.neighborhood } : null;
    } catch {
      return null;
    }
  },

  checkBusbarConflict: async (poleBusbar: string): Promise<{ conflict: boolean; protocolNumber?: string; existingOrderId?: number; status?: string }> => {
    try {
      const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/busbar-conflict/${encodeURIComponent(poleBusbar.trim().toUpperCase())}`);
      if (!response.ok) return { conflict: false };
      return response.json();
    } catch {
      return { conflict: false };
    }
  },

  searchPublicOrders: async (cpf: string): Promise<Order[]> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/orders/public?cpf=${encodeURIComponent(cpf)}`);
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar ordens do público'));
    const json = await response.json();
    return json.data ?? json;
  },
};

export const materialService = {
  list: async (page = 1, perPage = 300): Promise<{ data: Material[]; total: number; page: number; totalPages: number }> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/materials?page=${page}&perPage=${perPage}`);
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar materiais'));
    const json = await response.json();
    return { data: json.data ?? json, total: json.total ?? 0, page: json.page ?? 1, totalPages: json.totalPages ?? 1 };
  },

  create: async (data: CreateMaterialRequest): Promise<Material> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/materials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao cadastrar material'));
    return response.json();
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}/api/materials/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao excluir material'));
  },
};

const createCatalogService = (basePath: string) => ({
  list: async (page = 1, perPage = 300): Promise<{ data: Material[]; total: number; page: number; totalPages: number }> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}${basePath}?page=${page}&perPage=${perPage}`);
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao buscar itens'));
    const json = await response.json();
    return { data: json.data ?? json, total: json.total ?? 0, page: json.page ?? 1, totalPages: json.totalPages ?? 1 };
  },

  create: async (data: CreateMaterialRequest): Promise<Material> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}${basePath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao cadastrar item'));
    return response.json();
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetchWithRefresh(`${API_BASE_URL}${basePath}/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) throw new Error(await parseErrorMessage(response, 'Falha ao excluir item'));
  },
});

export const lampadaService = createCatalogService('/api/lampadas');
export const refletorService = createCatalogService('/api/refletores');
export const bairroService = createCatalogService('/api/bairros');
